import React, { useCallback, useMemo } from 'react';
import { NotificationAction, TNotification } from '../types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Flex, Label } from '@zeal/web-ui';
import { useTranslation } from 'react-i18next';
import { useMarkAsRead, useNotifications } from '@app/data/Notifications';
import { PriorityBadge } from './PriorityBadge';
import { useNotificationClick } from './useNotificationAction';

dayjs.extend(relativeTime);

export function Notification({
	uuid,
	title,
	body,
	created_at,
	priority,
	seen,
	action,
	setIsNotificationsOpen,
}: TNotification & {
	setIsNotificationsOpen: (value: boolean) => void;
}) {
	const { t } = useTranslation('common');
	const { refetch } = useNotifications();
	const { doMarkAsRead } = useMarkAsRead({
		onSuccess: refetch,
	});

	const { applyOnClick } = useNotificationClick();
	const handleNotificationClick = useCallback(
		(action: NotificationAction) => {
			applyOnClick(action);
			setIsNotificationsOpen(false);
		},
		[applyOnClick, setIsNotificationsOpen]
	);

	const createdAt = useMemo(() => {
		return dayjs(created_at).fromNow(true);
	}, [created_at]);

	return (
		<Flex
			key={uuid}
			isColumn
			gap="2"
			className="py-4 px-5 border-b border-solid border-divider-default bg-surface-default cursor-pointer hover:bg-surface-subdued"
			onClick={() => {
				if (!seen) {
					doMarkAsRead(uuid);
				}
				handleNotificationClick(action);
			}}
		>
			<Flex gap="2" align="center" justify="between">
				<Flex gap="2">
					<Label.Mid200>{title}</Label.Mid200>
					<PriorityBadge priority={priority} />
				</Flex>
				{!seen && <div className="w-2 h-2 bg-icon-critical rounded-full" />}
			</Flex>
			<Label.Thin100>{body}</Label.Thin100>
			<Label.Thin100 variant="subdued">
				{t('notifications_sent_at', { time: createdAt })}
			</Label.Thin100>
		</Flex>
	);
}
